import { useCallback, useEffect, useState } from "react";
import { UtensilsCrossed, CalendarDays, Wallet, FileCheck2, RefreshCw, X, Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const CARD_META = {
  dinner_approved: { title: "Cena del viernes aprobada", key: "dinner_approved_count", Icon: UtensilsCrossed, color: "text-hz-cyan" },
  general_event_approved: { title: "Sábado aprobado", key: "general_event_approved_count", Icon: CalendarDays, color: "text-hz-blue" },
  pending_payment: { title: "Pendientes de pago", key: "pending_payment_count", Icon: Wallet, color: "text-hz-warning" },
  pending_review: { title: "Pendientes de revisión", key: "pending_review_count", Icon: FileCheck2, color: "text-hz-navy" },
};

export const OperationalCounters = ({ refreshKey = 0, source = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get("/admin/operational-counters");
      setData(res);
    } catch (err) {
      setError(apiError(err, "No se pudieron cargar los contadores."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <section data-testid={`operational-counters${source ? `-${source}` : ""}`} className="mb-6 rounded-xl border border-hz-gray/40 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-hz-navy">Contadores operativos</h3>
          <p className="text-xs text-hz-ink/60">Personas únicas. Las categorías NO son excluyentes.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="counters-refresh" className="font-semibold">
          {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Actualizar
        </Button>
      </div>
      {error && <p className="rounded-lg border border-hz-error/40 bg-hz-error/5 px-3 py-2 text-xs text-hz-error" role="alert">{error}</p>}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Object.entries(CARD_META).map(([listType, meta]) => {
          const Icon = meta.Icon;
          const value = data?.[meta.key];
          return (
            <button type="button" key={listType} data-testid={`counter-card-${listType}`}
                    onClick={() => setDrill(listType)}
                    className="group rounded-xl border border-hz-gray/40 bg-hz-bg px-4 py-3 text-left transition hover:border-hz-cyan hover:shadow-sm">
              <div className="flex items-center gap-2 text-xs font-semibold text-hz-ink/70">
                <Icon className={`h-4 w-4 ${meta.color}`} /> {meta.title}
              </div>
              <p className="mt-2 text-2xl font-black text-hz-navy" data-testid={`counter-value-${listType}`}>
                {loading && value === undefined ? "…" : (value ?? 0)}
              </p>
              <p className="mt-0.5 text-[11px] text-hz-ink/50 group-hover:text-hz-blue">Pulsar para ver listado</p>
            </button>
          );
        })}
      </div>
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl" data-testid={`counter-drill-${drill || ""}`}>
          <DialogHeader>
            <DialogTitle>{drill ? CARD_META[drill].title : ""}</DialogTitle>
          </DialogHeader>
          {drill && <OperationalCounterList listType={drill} onClose={() => setDrill(null)} />}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const OperationalCounterList = ({ listType, onClose }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/operational-counters/${listType}`, { params: { page, page_size: pageSize } });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [listType, page]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid={`counter-list-${listType}`}>
      <p className="mb-2 text-xs text-hz-ink/60">Total personas: <strong>{total}</strong></p>
      <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-hz-gray/40">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Estado pago</TableHead>
              <TableHead>Motivo inclusión</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={4} className="text-center text-xs text-hz-ink/50 py-6">Cargando…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-xs text-hz-ink/50 py-6">Sin resultados.</TableCell></TableRow>}
            {!loading && rows.map((r) => (
              <TableRow key={r.attendee_id} data-testid={`counter-list-row-${r.folio}`}>
                <TableCell className="font-mono text-xs">{r.folio}</TableCell>
                <TableCell className="text-xs font-semibold">{r.full_name}</TableCell>
                <TableCell className="text-xs">{r.payment_status}</TableCell>
                <TableCell className="text-xs text-hz-ink/70">{r.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs">
        <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Anterior</Button>
        <span className="text-hz-ink/60">Página {page} de {pages}</span>
        <Button variant="outline" size="sm" disabled={page >= pages || loading} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
      </div>
    </div>
  );
};

export default OperationalCounters;
